import logger from '@/utils/logger';
/**
 * 全局异常处理器
 * 捕获 JavaScript 运行时异常和未处理的 Promise 异常
 */
class GlobalErrorHandler {
  private isInitialized = false;

  private errorCount = 0;

  private readonly MAX_ERRORS_PER_MINUTE = 10;

  /**
   * 初始化全局异常监听
   */
  init() {
    if (this.isInitialized) {
      logger.warn('[GlobalErrorHandler] 已经初始化过了');
      return;
    }

    logger.info('[GlobalErrorHandler] 初始化全局异常监听');

    // 监听 JavaScript 运行时异常
    this.setupWindowErrorHandler();

    // 监听未处理的 Promise 异常
    this.setupUnhandledRejectionHandler();

    // 监听资源加载异常
    this.setupResourceErrorHandler();

    // 静默处理已知的 ResizeObserver 误报（Chromium 特性）：
    // "ResizeObserver loop limit exceeded" /
    // "ResizeObserver loop completed with undelivered notifications."
    // 这些通常由 UI 库下拉/浮层的测量与定位循环触发，非致命，可忽略
    this.setupResizeObserverErrorSilencer();
    this.setupConsoleErrorSilencer();

    this.isInitialized = true;
  }

  /**
   * 捕获并静默已知 ResizeObserver 误报，避免开发/运行时红屏遮罩
   */
  private setupResizeObserverErrorSilencer() {
    window.addEventListener(
      'error',
      (event: any) => {
        try {
          const msg = String(event?.message || event?.error?.message || '').toLowerCase();
          if (
            msg.includes('resizeobserver loop limit exceeded') ||
            msg.includes('resizeobserver loop completed with undelivered notifications')
          ) {
            event.stopImmediatePropagation?.();
            event.preventDefault?.();
            // 不继续派发，也不记录日志，直接吞掉
          }
        } catch {}
      },
      true // 捕获阶段，优先于其他监听器处理
    );

    // 捕获 Promise 形式的 ResizeObserver 误报
    window.addEventListener(
      'unhandledrejection',
      (event: any) => {
        try {
          const reason = event?.reason;
          const msg = String(reason?.message || reason || '').toLowerCase();
          if (
            msg.includes('resizeobserver loop limit exceeded') ||
            msg.includes('resizeobserver loop completed with undelivered notifications')
          ) {
            event.stopImmediatePropagation?.();
            event.preventDefault?.();
          }
        } catch {}
      },
      true
    );
  }

  /**
   * 覆盖 console.error，过滤已知 ResizeObserver 误报，避免 React Error Overlay 弹层
   */
  private setupConsoleErrorSilencer() {
    try {
      const original = console.error;
      // eslint-disable-next-line no-console
      console.error = (...args: any[]) => {
        try {
          const joined = args
            .map((a) => String(a))
            .join(' ')
            .toLowerCase();
          if (
            joined.includes('resizeobserver loop limit exceeded') ||
            joined.includes('resizeobserver loop completed with undelivered notifications')
          ) {
            return; // 吞掉该错误日志
          }
        } catch {}
        original.apply(console, args as unknown as Parameters<typeof original>);
      };
    } catch {}
  }

  /**
   * 设置 window.onerror 监听器
   */
  private setupWindowErrorHandler() {
    const originalOnError = window.onerror;

    window.onerror = (message, source, lineno, colno, error) => {
      // 调用原始的 onerror 处理器（如果存在）
      if (originalOnError) {
        originalOnError.call(window, message, source, lineno, colno, error);
      }

      // 记录错误信息
      const errorInfo = {
        type: 'window.onerror',
        message: String(message),
        source: String(source),
        lineno: Number(lineno),
        colno: Number(colno),
        stack: error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // 检查是否是RTC相关的已知错误
      if (this.isKnownRTCError(errorInfo)) {
        logger.warn('[GlobalErrorHandler] RTC 相关错误（已知问题）:', errorInfo);
        // RTC 错误通常是内部竞争条件，不需要显示给用户
        return true;
      }

      logger.error('[GlobalErrorHandler] JavaScript 运行时异常:', errorInfo);

      // 上报错误
      this.reportError(errorInfo);

      // 显示用户友好的错误提示
      this.showUserFriendlyError();

      // 返回 true 表示错误已被处理，阻止默认的错误处理
      return true;
    };
  }

  /**
   * 检查是否是已知的RTC错误
   */
  private isKnownRTCError(errorInfo: any): boolean {
    const message = String(errorInfo.message).toLowerCase();

    // 已知的RTC相关错误模式
    const knownRTCErrors = [
      "cannot read properties of undefined (reading 'connectionstate')",
      "cannot read properties of undefined (reading 'connectionstate')",
      'peerconnection',
      'rtc',
      'webrtc',
      'stream closed',
      'invalid state error',
      'connectionstate',
      'peer connection',
      'webrtc connection',
    ];

    return knownRTCErrors.some((pattern) => message.includes(pattern));
  }

  /**
   * 设置 unhandledrejection 监听器
   */
  private setupUnhandledRejectionHandler() {
    const originalOnUnhandledRejection = window.onunhandledrejection;

    window.addEventListener('unhandledrejection', (event) => {
      // 调用原始的处理器（如果存在）
      if (originalOnUnhandledRejection) {
        originalOnUnhandledRejection.call(window, event);
      }

      // 记录错误信息
      const errorInfo = {
        type: 'unhandledrejection',
        reason: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      logger.error('[GlobalErrorHandler] 未处理的 Promise 异常:', errorInfo);

      // 上报错误
      this.reportError(errorInfo);

      // 显示用户友好的错误提示
      this.showUserFriendlyError();

      // 阻止默认的错误处理
      event.preventDefault();
    });
  }

  /**
   * 设置资源加载异常监听器
   */
  private setupResourceErrorHandler() {
    window.addEventListener(
      'error',
      (event) => {
        // 只处理资源加载错误（如图片、脚本、样式表等）
        if (event.target && event.target !== window) {
          const target = event.target as HTMLElement;
          const errorInfo = {
            type: 'resource.error',
            tagName: target.tagName,
            src: (target as any).src || (target as any).href,
            timestamp: new Date().toISOString(),
            url: window.location.href,
          };

          logger.warn('[GlobalErrorHandler] 资源加载失败:', errorInfo);

          // 资源加载失败通常不需要弹窗提示用户，只记录日志
          this.reportError(errorInfo);
        }
      },
      true
    ); // 使用捕获阶段
  }

  /**
   * 上报错误信息
   */
  private reportError(errorInfo: any) {
    try {
      // 简化频率控制：超过阈值直接丢弃，不再使用定时器重置
      this.errorCount = Math.min(this.errorCount + 1, this.MAX_ERRORS_PER_MINUTE + 1);
      if (this.errorCount > this.MAX_ERRORS_PER_MINUTE) return;

      // 这里可以集成错误上报服务，如 Sentry
      logger.warn('[GlobalErrorHandler] 错误上报数据:', errorInfo);

      // 可以发送到后端或第三方服务
      // fetch('/api/error-report', { // 错误报告 API 调用
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorInfo),
      // }).catch(console.warn);
    } catch (reportError) {
      logger.warn('[GlobalErrorHandler] 错误上报失败:', reportError);
    }
  }

  /**
   * 显示用户友好的错误提示
   */
  private showUserFriendlyError() {
    // K12 场景：不弹窗，只记录日志
    // 错误消息显示
    logger.warn('[GlobalErrorHandler] 错误发生，但K12场景下不弹窗，仅日志记录');
    // 可选：未来可集成顶部横幅提示
  }

  /**
   * 手动触发错误（用于测试）
   */
  testError() {
    logger.info('[GlobalErrorHandler] 触发测试错误');
    throw new Error('这是一个测试错误');
  }

  /**
   * 手动触发 Promise 错误（用于测试）
   */
  testPromiseError() {
    logger.info('[GlobalErrorHandler] 触发测试 Promise 错误');
    Promise.reject(new Error('这是一个测试 Promise 错误'));
  }
}

// 创建单例实例
const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
